import React, { ReactNode, createContext, useContext, useMemo, useReducer } from "react";

import { produce } from "immer";

import { EntityKind } from "../../../models/entityKind";
import { sessionStorageUwidFormat } from "../../../utils/sessionStorageUwidFormat";
import { WellListReducerActionsT, wellListReducer } from "./reducer/wellListReducer";

export type WellInfoT = {
  id: string;
  label: string;
};

export type WellGroupInfoT = {
  group?: string;
  color?: string;
};

export type UwidFormatT = "entityName" | "uwid" | "api10" | "mosaic";
export type CopyWellListFormatT = "newLine" | "csv" | "mongo";
export type CopyWellListQuoteTypeT = "none" | "single" | "double";

export type SelectedWellT = {
  Uwi?: string;
  FormattedUwi?: string;
};

export interface IWellGroupMap {
  [uwid: string]: WellGroupInfoT;
}

export type WellListSettings = {
  scrollToSelected: boolean;
  showFocusColor: boolean;
  uwidFormat: UwidFormatT;
  copyFormat: CopyWellListFormatT;
  copyQuoteType?: CopyWellListQuoteTypeT;
};

export interface IWellListState {
  previouslySelectedWells: SelectedWellT[];
  selectedWell?: SelectedWellT;
  settings: WellListSettings;
  wells: WellInfoT[];
  totalCount: number;
  wellGroupMap?: IWellGroupMap;
  entityKind: EntityKind;
  // Sparse pages fetched on-demand while scrolling the virtualized list
  // (WellListList), keyed by page index. Shared here — rather than kept as
  // component-local state — so "fetch all remaining wells" (WellList) can
  // reuse pages already loaded instead of re-fetching them from scratch.
  // A plain record (not a Map) because Immer's Map/Set support isn't
  // enabled in this app (no `enableMapSet()` call).
  wellPages: Record<number, WellInfoT[]>;
}

export const initialWellListState: IWellListState = {
  previouslySelectedWells: [],
  settings: {
    scrollToSelected: true,
    showFocusColor: true,
    uwidFormat: sessionStorageUwidFormat.get(),
    copyFormat: "newLine",
    copyQuoteType: "single"
  },
  entityKind: EntityKind.Well,
  wells: [],
  totalCount: 0,
  wellPages: {}
};

export type WellListDispatchT = (action: WellListReducerActionsT) => void;
export type WellListProviderT = {
  children: ReactNode;
  initialState?: IWellListState;
};

const curriedReducer = produce(wellListReducer);

const WellListContext =
  createContext<[IWellListState, React.Dispatch<WellListReducerActionsT>]>(undefined);
WellListContext.displayName = "WellListContext";

const WellListProvider = ({ children, initialState }: WellListProviderT) => {
  const newState = initialState ?? initialWellListState;

  const [state, dispatch] = useReducer(curriedReducer, newState);
  const value: [IWellListState, WellListDispatchT] = useMemo(
    () => [state, dispatch],
    [state]
  );

  return <WellListContext.Provider value={value}>{children}</WellListContext.Provider>;
};

const useWellList = () => {
  const context = useContext(WellListContext);
  if (context === undefined) {
    throw new Error("useWellList must be used within WellListProvider");
  }
  return context;
};

export { curriedReducer, WellListProvider, useWellList };
